import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Histogram from '@/components/common/Histogram/Histogram';
import RangeSlider from '@/components/switchback/RangeSlider/RangeSlider';
import TextInput from '@/components/switchback/TextInput';
import { ESearchFilters } from '@/constants/searchFilters';
import { STRINGS } from '@/constants/strings';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { formatCurrency } from '@/utility/currency';
import { clamp } from '@/utility/numbers';

interface IProps {
  currency: string;
  histogramValues: number[];
  minValue?: number;
  maxValue: number;
  defaultValue: [number, number];
  show: boolean;
  onChange: (minValue: number, maxValue: number) => void;
  onChangeCommitted?: (minValue: number, maxValue: number, fromInput?: boolean) => void;
  isInMoreFilter?: boolean;
}

const PriceFilter: React.FC<IProps> = ({
  currency,
  defaultValue,
  histogramValues,
  minValue = 0,
  maxValue,
  show,
  onChange,
  onChangeCommitted,
  isInMoreFilter = false,
}) => {
  const intl = useIntl();
  // The formatted value fills an input and is parsed back to a number, so it carries no ISO code.
  const currencyFormat = useMemo(
    () => ({ currency, digits: 0, hideCurrencyCode: true }),
    [currency],
  );
  const getFormatValue = useCallback(
    (value: number) => {
      const inbetweenValue = clamp(value, minValue, maxValue);
      return `${formatCurrency({
        priceInCents: inbetweenValue || 0,
        ...currencyFormat,
      })}${value >= maxValue ? STRINGS.PLUS : ''}`;
    },
    [currencyFormat, maxValue, minValue],
  );
  const [value, setValue] = useState<[number, number]>(defaultValue || [minValue, maxValue]);
  const [currencyValue, setCurrencyValue] = useState(
    (defaultValue || [minValue, maxValue]).map(val => getFormatValue(val)),
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // Get query params and check for date presence
  const queryParams = useSelector(getQueryParams);
  const hasDates = !!queryParams[ESearchFilters.DATE_FROM] && !!queryParams[ESearchFilters.DATE_TO];

  const filterTitle = hasDates
    ? intl.formatMessage({ defaultMessage: 'Estimated total price', id: 'cM4exE' })
    : intl.formatMessage({ defaultMessage: 'Price per night', id: '5xvHlh' });

  const handleChangeSlider = (newValue: number | number[]) => {
    if (
      !Array.isArray(newValue) ||
      typeof newValue[0] === 'undefined' ||
      typeof newValue[1] === 'undefined'
    ) {
      return;
    }

    setValue([newValue[0], newValue[1]]);
    onChange?.(newValue[0] || 0, newValue[1] || 100);
  };

  const handleChangeCommittedSlider = (newValue: number | number[]) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    onChangeCommitted?.(newValue[0] || 0, newValue[1] || 100);
  };

  const handleBlurInput = (newValue: string, index: 0 | 1) => {
    const isEmpty = newValue.trim() === '';
    const parsed = Number(newValue.replace(/[^0-9.-]+/g, ''));
    const valueNumber = clamp(Math.round(parsed || 0), minValue, maxValue) * 100;

    const updatedValue: [number, number] = [...value];
    updatedValue[index] = isEmpty ? (index === 0 ? minValue : maxValue) : valueNumber;

    const newCurrencyValue = Array.from(currencyValue);
    newCurrencyValue[index] = isEmpty ? '' : getFormatValue(updatedValue[index]);
    setCurrencyValue(newCurrencyValue);

    if (isEqual(updatedValue, value)) return;

    setValue(updatedValue);
    onChange?.(updatedValue[0], updatedValue[1]);
    onChangeCommitted?.(updatedValue[0], updatedValue[1], true);
  };

  const handleBlurMin = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleBlurInput(event.currentTarget.value, 0);
  };

  const handleBlurMax = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleBlurInput(event.currentTarget.value, 1);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: 0 | 1) => {
    const newCurrencyValue = [...currencyValue];
    newCurrencyValue[index] = event.currentTarget.value;
    setCurrencyValue(newCurrencyValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: 0 | 1) => {
    if (event.key === 'Enter') {
      handleBlurInput((event.target as HTMLInputElement).value, index);
    }
  };

  // debounce update view value
  useEffect(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setCurrencyValue(value.map(val => getFormatValue(val)));
    }, 750);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [value, getFormatValue]);

  // update from default value whenever filter opened
  useEffect(() => {
    if (show) {
      setValue(defaultValue);
      setCurrencyValue(defaultValue.map(val => getFormatValue(val)));
    }
  }, [show, defaultValue, getFormatValue]);

  return (
    <div className="flex flex-col">
      {isInMoreFilter ? (
        <>
          <Text
            className={clsx('text-base font-medium', { 'mb-4': !hasDates })}
            variant={ETextStyleVariant.SmallBold}>
            {filterTitle}
          </Text>
          {hasDates && (
            <Text className="mb-4 text-gray-50" variant={ETextStyleVariant.SmallRegular}>
              {intl.formatMessage({ defaultMessage: 'Includes fees and insurance.', id: 'nrXFCm' })}
            </Text>
          )}
        </>
      ) : (
        <>
          <h3 className={clsx('text-base font-medium', { 'mb-4': !hasDates })}>{filterTitle}</h3>
          {hasDates && (
            <Text className="mb-4 text-gray-50" variant={ETextStyleVariant.MediumRegular}>
              {intl.formatMessage({ defaultMessage: 'Includes fees and insurance.', id: 'nrXFCm' })}
            </Text>
          )}
        </>
      )}
      <div
        className={clsx('flex items-center mb-6 md:mb-8', {
          'w-full': isInMoreFilter,
        })}>
        <TextInput
          className={clsx('w-full md:w-28', { 'flex-1': isInMoreFilter })}
          name="price-filter-min"
          onBlur={handleBlurMin}
          onChange={e => handleChange(e, 0)}
          onKeyDown={e => handleKeyDown(e, 0)}
          value={currencyValue[0]}
          label={isInMoreFilter ? 'Min' : undefined}
        />
        <span className="mx-4 autoType400">-</span>
        <TextInput
          className={clsx('w-full md:w-28', { 'flex-1': isInMoreFilter })}
          name="price-filter-max"
          onBlur={handleBlurMax}
          onChange={e => handleChange(e, 1)}
          onKeyDown={e => handleKeyDown(e, 1)}
          value={currencyValue[1]}
          label={isInMoreFilter ? 'Max' : undefined}
        />
      </div>
      <div
        className={clsx('h-20', {
          'w-full': isInMoreFilter,
          'px-7': !isInMoreFilter,
        })}>
        <Histogram
          values={histogramValues}
          minValue={minValue}
          maxValue={maxValue}
          highlightRange={{
            min: value[0],
            max: value[1],
          }}
        />
      </div>
      <div
        className={clsx({
          'w-full': isInMoreFilter,
          'md:mb-4': !isInMoreFilter,
          'px-7': !isInMoreFilter,
        })}>
        <RangeSlider
          onChange={handleChangeSlider}
          onChangeCommitted={handleChangeCommittedSlider}
          minValue={minValue}
          maxValue={maxValue}
          defaultValue={value}
          step={100}
        />
      </div>
    </div>
  );
};

export default PriceFilter;
